import { useState } from "react";
import { CalendarX2 } from "lucide-react";
import * as devicesApi from "../../api/devices";
import { RelazioneCliente } from "../../api/devices";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { etichettaMatricola } from "../../utils/matricola";

/**
 * La data di oggi in formato `YYYY-MM-DD`, calcolata sul fuso LOCALE.
 *
 * Non `toISOString().slice(0, 10)`, che è la forma usata altrove nel frontend:
 * quella converte in UTC, e in Italia fra mezzanotte e le 01:00 (le 02:00 con
 * l'ora legale) restituisce IERI. Su un campo che l'utente legge come "oggi" e
 * che finisce in una colonna `@db.Date` dello storico, un giorno di sfasamento
 * non si nota al momento e non si spiega più dopo.
 */
function oggiISO(): string {
  const d = new Date();
  const mese = String(d.getMonth() + 1).padStart(2, "0");
  const giorno = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mese}-${giorno}`;
}

function dataIT(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

/**
 * Chiude il rapporto fra una cassaforte e uno dei clienti che la usano.
 *
 * NON cancella niente: valorizza `data_fine` sulla riga di `device_cliente`, che
 * è il modo in cui questo sistema ha sempre inteso la fine di una relazione —
 * la riga resta leggibile con la sede, il codice GDO e il servizio di quel
 * periodo. Vale anche per le casseforti condivise: si stacca UN cliente, gli
 * altri restano dove sono e il pezzo non si sposta di un metro.
 *
 * Non è previsto il contrario da qui. Riaprire un rapporto chiuso vuol dire
 * aprire una riga nuova (dalla Modifica del device), non svuotare `data_fine`:
 * altrimenti il periodo in cui il cliente non c'era sparirebbe dallo storico.
 */
export function TerminaRapporto({
  open,
  onClose,
  relazione,
  matricola,
  /** Quante relazioni sono aperte in tutto, questa compresa. */
  aperteInTutto,
  onTerminato,
}: {
  open: boolean;
  onClose: () => void;
  relazione: RelazioneCliente;
  matricola: string | null;
  aperteInTutto: number;
  onTerminato: () => void;
}) {
  const { showToast } = useToast();
  const [data, setData] = useState(oggiISO);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  /**
   * L'inizio del rapporto, quando lo conosciamo. È nullo su TUTTE le relazioni
   * arrivate dall'import storico, quindi il controllo qui sotto non si può
   * dare per scontato: senza data di inizio non c'è niente da confrontare.
   */
  const inizio = relazione.data_inizio ? relazione.data_inizio.slice(0, 10) : null;

  async function conferma() {
    if (!data) {
      setErrore("Serve una data: una relazione chiusa senza sapere quando non si legge più.");
      return;
    }
    // Una fine prima dell'inizio non è un errore di battitura che si aggiusta
    // dopo: resta nello storico come un rapporto durato meno di zero giorni, e
    // il database non ha nulla che lo impedisca.
    if (inizio && data < inizio) {
      setErrore(`La fine non può precedere l'inizio del rapporto (${dataIT(relazione.data_inizio)}).`);
      return;
    }

    setErrore(null);
    setInCorso(true);
    try {
      await devicesApi.updateDeviceCliente(relazione.id, { data_fine: data });
      showToast(`Rapporto con ${relazione.cliente.nome} terminato il ${dataIT(data)}.`, "success");
      onTerminato();
      onClose();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile terminare il rapporto."), "error");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Termina rapporto — ${relazione.cliente.nome}`}
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="button" onClick={conferma} loading={inCorso}>
            <CalendarX2 className="h-4 w-4" aria-hidden="true" />
            Termina rapporto
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink/70">
          <span className="font-semibold text-ink">{relazione.cliente.nome}</span> smette di usare la cassaforte{" "}
          <span className="font-semibold text-ink">{etichettaMatricola(matricola)}</span>
          {relazione.sede_cliente?.nome_sede && <> presso {relazione.sede_cliente.nome_sede}</>}.
        </p>

        <Input
          label="Data di fine"
          type="date"
          value={data}
          onChange={(e) => {
            setData(e.target.value);
            setErrore(null);
          }}
          error={errore ?? undefined}
          hint={
            errore
              ? undefined
              : inizio
                ? `Rapporto iniziato il ${dataIT(relazione.data_inizio)}.`
                : "Data di inizio non registrata per questo rapporto."
          }
        />

        <div className="rounded-lg border border-line bg-line/10 p-3 text-xs leading-relaxed text-ink/60">
          <p>
            Il rapporto resta nello storico della cassaforte, marcato come chiuso: sede, codice GDO e servizio di questo
            periodo continuano a leggersi dalla scheda. Non viene cancellato niente.
          </p>
          <p className="mt-2">
            {/* Verificato su buildScopedWhere (routes/devices.ts): lo scope di un
                utente `cliente` filtra su `clienti: { some: { cliente_id } }`
                SENZA guardare `data_fine`. Una riga chiusa continua quindi a
                dare visibilità — è la cancellazione, non la chiusura, che
                toglie il device dalla vista del cliente. Scritto qui perché è
                esattamente la domanda che uno si fa premendo il pulsante. */}
            Se il cliente ha un accesso all&apos;app continuerà a vedere questa cassaforte nel proprio storico: quello
            che cambia è che non risulta più fra i clienti serviti oggi.
          </p>
          {aperteInTutto > 1 ? (
            <p className="mt-2">
              Gli altri {aperteInTutto - 1} clienti di questa cassaforte non vengono toccati: si stacca solo questo.
            </p>
          ) : (
            <p className="mt-2">
              È l&apos;ultimo cliente collegato: dopo la chiusura la cassaforte resta censita e attiva, ma senza nessun
              rapporto in corso. Se è anche uscita di servizio, va impostata come dismessa dalla Modifica — sono due
              fatti diversi e nessuno dei due implica l&apos;altro.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
